/**
 * @file The states that defer an automatic reload (docs/decisions/0002). Each one names work a full
 * page load would destroy. A user's Reload click warns on any of them before it reloads.
 */

interface AxiosLike {
  defaults: { adapter?: unknown }
}

type Adapter = (config: { method?: string }) => Promise<unknown>

const READ_METHODS = new Set(['get', 'head', 'options'])

/** Counts API writes in flight across every axios instance it is installed on. */
export function createWriteTracker() {
  const wrappers = new WeakSet<Adapter>()
  let inFlight = 0

  // Wraps the adapter, not interceptors: a request cancelled before dispatch never reaches it.
  function install(instance: AxiosLike) {
    const adapter = instance.defaults.adapter as Adapter | undefined
    if (typeof adapter !== 'function' || wrappers.has(adapter)) return
    const wrapped: Adapter = async config => {
      if (READ_METHODS.has((config.method ?? 'get').toLowerCase())) {
        return adapter(config)
      }
      inFlight++
      try {
        return await adapter(config)
      } finally {
        inFlight--
      }
    }
    wrappers.add(wrapped)
    instance.defaults.adapter = wrapped
  }

  return { install, count: () => inFlight }
}

const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT'])

function isEditable(target: EventTarget | null): target is Element {
  if (!(target instanceof Element)) return false
  if (target instanceof HTMLElement && target.isContentEditable) return true

  return EDITABLE_TAGS.has(target.tagName)
}

// An edit before any focus (autofill, a label click) has no trustworthy baseline: always changed.
const NO_BASELINE = Symbol('no baseline')
type Baseline = string | boolean | typeof NO_BASELINE

// The value a v-model sets is not the DOM default, so the baseline is read at first focus.
function currentValue(element: Element): string | boolean | null {
  if (element instanceof HTMLInputElement) {
    return ['checkbox', 'radio'].includes(element.type)
      ? element.checked
      : element.value
  }
  if (element instanceof HTMLTextAreaElement) return element.value

  return null
}

/** Fields the user changed that are still on the page; a closed modal or a left route drops them. */
export function createEditTracker(doc: Document) {
  const edited = new Set<Element>()
  const baselines = new WeakMap<Element, Baseline>()
  const onFocus = (event: Event) => {
    const { target } = event
    if (!isEditable(target) || baselines.has(target)) return
    const value = currentValue(target)
    if (value !== null) baselines.set(target, value)
  }
  // Back at its first-focus value (a sent chat message) is clean; selects and rich text stay changed.
  const stillChanged = (element: Element) => {
    const value = currentValue(element)

    return value === null || value !== baselines.get(element)
  }
  const dropDetached = () =>
    edited.forEach(element => {
      if (!element.isConnected) edited.delete(element)
    })
  // Pruning on add too keeps closed modals' nodes from piling up over a long day.
  const onEdit = (event: Event) => {
    if (!isEditable(event.target) || edited.has(event.target)) return
    if (!baselines.has(event.target)) baselines.set(event.target, NO_BASELINE)
    dropDetached()
    edited.add(event.target)
  }
  doc.addEventListener('focusin', onFocus, true)
  doc.addEventListener('input', onEdit, true)
  doc.addEventListener('change', onEdit, true)

  function hasUnsavedInput() {
    dropDetached()

    return [...edited].some(stillChanged)
  }

  function stop() {
    doc.removeEventListener('focusin', onFocus, true)
    doc.removeEventListener('input', onEdit, true)
    doc.removeEventListener('change', onEdit, true)
  }

  return { hasUnsavedInput, stop }
}

export interface ReloadSafetyInputs {
  doc: Document
  writesInFlight: () => number
  hasUnsavedInput: () => boolean
  routeName: () => string | null | undefined
  dataStore: () => { isReportsFlyoutOpen?: boolean; csvData?: unknown }
}

const CSV_IMPORT_ROUTES = new Set(['CustomerList', 'CustomerImport'])

function hasCsvData(csvData: unknown): boolean {
  return (
    !!csvData && typeof csvData === 'object' && Object.keys(csvData).length > 0
  )
}

/**
 * The pending unsafe states, by name; empty means a reload loses nothing. This app has no PIN pad.
 * The CSV import is the one multi-route flow whose store data a reload loses.
 */
export function unsafeReloadReasons(inputs: ReloadSafetyInputs): string[] {
  const { doc } = inputs
  const store = inputs.dataStore()
  const checks: [string, boolean][] = [
    ['api-write', inputs.writesInFlight() > 0],
    ['unsaved-input', inputs.hasUnsavedInput()],
    ['card-entry', doc.querySelector('[data-vgs-field]') !== null],
    ['modal-open', doc.querySelector('.modal.show') !== null],
    ['drawer-open', !!store.isReportsFlyoutOpen],
    [
      'csv-import',
      CSV_IMPORT_ROUTES.has(inputs.routeName() ?? '') &&
        hasCsvData(store.csvData)
    ]
  ]

  return checks.filter(([, unsafe]) => unsafe).map(([name]) => name)
}
